import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import "./ReminderPopup.css";
import { API_BASE } from "../apiConfig";

// ─── LOGIC ──────────────────────────────────────────────────────────────────
//
//  1. FRESH LOGIN  → show today's un-acknowledged reminders, one by one.
//  2. "Ok, Got it" → acknowledge that reminder for TODAY. It will NOT appear
//                    again in this session, on a re-login today, or tomorrow
//                    (tomorrow the date filter naturally excludes it anyway).
//  3. "Remind me later" → close popup; show it again after 3 hours in the
//                         SAME login session.
//  4. LOGIN SCREEN → ReminderPopup is never rendered there (App.js gate).
//  5. PAGE RELOAD (same session) → respect the remaining 3-hour wait; do NOT
//                                   re-show immediately.
//
// ────────────────────────────────────────────────────────────────────────────

// How long to wait before re-showing after "Remind me later".
const SNOOZE_MS = 3 * 60 * 60 * 1000;

// localStorage keys
const NEXT_SHOW_KEY  = "reminderNextShowAt";   // epoch ms of the next scheduled check
const LAST_TOKEN_KEY = "reminderLastToken";     // detects a fresh login vs page reload

// sessionStorage key set by login.js right before redirecting to /Home.
const LOGIN_FLAG_KEY = "reminderShowOnLoad";

// Keys written by older versions of this file – cleared on mount so they
// cannot accidentally block the current schedule.
const LEGACY_KEYS = [
  "reminderSnoozedUntil",
  "reminderLastShownAt",
  "reminderDismissedAt",
  "reminderFreshLoginFetch",  // added/removed in a previous iteration
];

const clearLegacyKeys = () =>
  LEGACY_KEYS.forEach((k) => { localStorage.removeItem(k); sessionStorage.removeItem(k); });

// Guard: an expired or absent token must never trigger the popup.
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload || !payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return false;
  }
};

// The therapist's own calendar day as YYYY-MM-DD.
//
// The API's default "today" is the server day (the project runs on
// Australia/Sydney). A therapist in another timezone can be a day behind:
// 28 Oct 20:00 IST is already 29 Oct in Sydney, so a goal whose target date is
// 29/10 was raised as a "today" reminder on the 28th. The popup therefore
// tells the API which day it is where the therapist actually is, and records
// "Ok, Got it" against that same day.
const getLocalToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// ─── COMPONENT ──────────────────────────────────────────────────────────────

const ReminderPopup = () => {
  const [dueReminders, setDueReminders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPopup,    setShowPopup]    = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [showReadMore,  setShowReadMore]  = useState(false);

  const textRef      = useRef(null);
  const gotItRef     = useRef(null);
  const timerRef     = useRef(null);
  const cancelledRef = useRef(false);
  const navigate     = useNavigate();

  // ── helpers ───────────────────────────────────────────────────────────────

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Fetch today's reminders from the server and show the popup if any exist.
  // The server only returns reminders for TODAY that have NOT yet been
  // acknowledged / dismissed by "Ok, Got it". "Remind me later" does NOT
  // acknowledge, so those reminders reappear on the next scheduled check.
  const fetchAndShow = async (token) => {
    try {
      const today = getLocalToday();
      const response = await fetch(
        `${API_BASE}/therapist_api/get_today_reminders.php?today=${encodeURIComponent(today)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (cancelledRef.current) return;

      if (data.status === "success" && Array.isArray(data.data)) {
        // Defensive: if an older API build ignores ?today and answers with the
        // server's day, never show a card whose own date is not the
        // therapist's today.
        const todaysReminders = data.data.filter(
          (item) => String(item.reminder_date || "").slice(0, 10) === today
        );

        if (todaysReminders.length > 0) {
          setDueReminders(todaysReminders);
          setCurrentIndex(0);
          setShowPopup(true);
        }
      }
    } catch (error) {
      console.error("Error fetching today's reminders:", error);
    } finally {
      // Record when the next automatic check should run (3 hours from now).
      localStorage.setItem(NEXT_SHOW_KEY, String(Date.now() + SNOOZE_MS));
    }
  };

  // Schedule a future fetch-and-show after `delay` ms, then repeat every
  // SNOOZE_MS.  Used by "Remind me later" and the page-reload path.
  const scheduleNext = (delay) => {
    clearTimer();
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      if (cancelledRef.current) return;

      const token = localStorage.getItem("token");
      if (!token || isTokenExpired(token)) return;

      await fetchAndShow(token);
      scheduleNext(SNOOZE_MS);
    }, Math.max(0, delay));
  };

  // Show the popup immediately (fresh login or newly created reminder).
  const showNow = (token) => {
    if (cancelledRef.current) return;
    clearLegacyKeys();
    localStorage.removeItem(NEXT_SHOW_KEY);
    fetchAndShow(token);
    scheduleNext(SNOOZE_MS);
  };

  // ── mount effect ──────────────────────────────────────────────────────────

  useEffect(() => {
    cancelledRef.current = false;

    const token = localStorage.getItem("token");
    if (!token || isTokenExpired(token)) {
      // No valid token — nothing to do (login screen or expired session).
      return () => { cancelledRef.current = true; clearTimer(); };
    }

    // Determine whether this is a fresh login or a page reload.
    //   Fresh login  → login.js sets sessionStorage "reminderShowOnLoad" = "1"
    //                  before redirecting, OR the stored token changed.
    //   Page reload  → same token, no session flag → respect the 3-hour timer.
    const loginFlag   = sessionStorage.getItem(LOGIN_FLAG_KEY) === "1";
    sessionStorage.removeItem(LOGIN_FLAG_KEY);

    const prevToken    = localStorage.getItem(LAST_TOKEN_KEY);
    const isFreshLogin = prevToken !== token;

    if (isFreshLogin || loginFlag) {
      // Save token so subsequent reloads in this session are not treated as
      // fresh logins.
      localStorage.setItem(LAST_TOKEN_KEY, token);
      // Show popup immediately for any un-acknowledged reminders today.
      showNow(token);
    } else {
      // Page reload within the same session: wait out the remaining snooze.
      const nextShowAt = Number(localStorage.getItem(NEXT_SHOW_KEY) || 0);
      const delay = nextShowAt > Date.now() ? nextShowAt - Date.now() : 0;
      scheduleNext(delay);
    }

    return () => {
      cancelledRef.current = true;
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── derived state ─────────────────────────────────────────────────────────

  const currentReminder = dueReminders[currentIndex];
  const message = (currentReminder && currentReminder.reminder_message) || "";

  // Show "Read more" link when message overflows the text box.
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    setShowReadMore(el.scrollHeight > el.clientHeight + 1);
  }, [showPopup, currentIndex, message]);

  // Move focus to the primary button whenever a new reminder appears.
  useEffect(() => {
    if (showPopup && gotItRef.current) gotItRef.current.focus();
  }, [showPopup, currentIndex]);

  // Esc key → "Remind me later".
  useEffect(() => {
    if (!showPopup) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      handleRemindLater();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup]);

  // ── popup state helpers ───────────────────────────────────────────────────

  const closeAll = () => {
    setShowPopup(false);
    setDueReminders([]);
    setCurrentIndex(0);
  };

  const showNext = () => {
    setCurrentIndex((prev) => {
      if (prev + 1 < dueReminders.length) {
        return prev + 1;
      }
      closeAll();
      return 0;
    });
  };

  // ── button handlers ───────────────────────────────────────────────────────

  // "Ok, Got it": acknowledge this reminder for TODAY.
  // Server records the acknowledgement so it won't appear again today
  // (even if the user logs out and back in).  Tomorrow the date filter
  // naturally excludes it, so no further action is needed.
  const handleGotIt = async () => {
    if (!currentReminder || acknowledging) return;
    setAcknowledging(true);

    const token = localStorage.getItem("token");
    // Acknowledge against the same day the popup was fetched for, so the
    // dismissal matches on a re-login today even when the server (Sydney) has
    // already rolled over to tomorrow.
    const today = getLocalToday();
    try {
      if (currentReminder.type === "goal") {
        // Goal reminders: store a dismiss record (dismiss_date = today).
        await fetch(`${API_BASE}/therapist_api/bulk_delete.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ids: [currentReminder.id], dismiss_date: today }),
        });
      } else {
        // Manual reminders: record acknowledgement (ack_date = today).
        await fetch(`${API_BASE}/therapist_api/ack_reminders.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reminder_ids: [currentReminder.id], ack_date: today }),
        });
      }
    } catch (error) {
      console.error("Error acknowledging reminder:", error);
    }

    setAcknowledging(false);
    showNext();
  };

  // "Remind me later": advance to the next reminder in queue.
  // Does NOT acknowledge on the server, so any snoozed reminder will reappear
  // on the next 3-hour check.
  const handleRemindLater = () => {
    scheduleNext(SNOOZE_MS);
    showNext();
  };

  // "Read more": navigate to the Reminders page (snooze first).
  const handleReadMore = () => {
    scheduleNext(SNOOZE_MS);
    closeAll();
    navigate("/Reminder");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      handleRemindLater();
    }
  };

  // ── render ────────────────────────────────────────────────────────────────

  if (!showPopup || !currentReminder) return null;

  return (
    <div
      className="reminder-popup-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Reminder notification"
      onKeyDown={handleKeyDown}
    >
      <div className="reminder-popup-card">
        {dueReminders.length > 1 && (
          <div
            style={{
              fontSize: "13px",
              color: "#626A6F",
              fontWeight: 600,
              marginBottom: "8px",
            }}
          >
            Reminder {currentIndex + 1} of {dueReminders.length}
          </div>
        )}
        <div className="reminder-popup-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faBell} aria-hidden="true" />
        </div>
        <p ref={textRef} className="reminder-popup-text">
          {message}
        </p>
        {showReadMore && (
          <button
            className="reminder-popup-readmore"
            onClick={handleReadMore}
            type="button"
          >
            Read more...
          </button>
        )}
        <div className="reminder-popup-actions">
          <button
            ref={gotItRef}
            className="reminder-popup-btn-gotit"
            onClick={handleGotIt}
            disabled={acknowledging}
            type="button"
          >
            {acknowledging ? "Saving..." : "Ok, Got it"}
          </button>
          <button
            className="reminder-popup-btn-later"
            onClick={handleRemindLater}
            type="button"
          >
            Remind me later
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReminderPopup;
